import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Paper, Stack, Typography, Switch, Table, TableHead, TableRow, TableCell,
  Chip, Alert, TextField, InputAdornment, MenuItem, LinearProgress, Button,
} from '@mui/material';
import DemoDataScopeDialog from './DemoDataScopeDialog';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import TableBody from './PagedTableBody';
import {
  DEMO_DATA_STATUS_QUERY, SET_DEMO_DATA_HIDDEN_MUTATION, DEACTIVATE_USER_MUTATION, REACTIVATE_USER_MUTATION,
  RECREATE_DEMO_DATA_MUTATION, DELETE_DEMO_DATA_MUTATION,
} from '../graphql/queries';

// Company settings: hide the seed (demo) data except one admin, then switch individual demo
// accounts back on (or off) one by one. A deactivated account cannot sign in and its vendor
// offerings, profile and family are not listed anywhere; nothing is deleted.
export default function DemoDataSettings() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [message, setMessage] = useState(null);
  const { data, loading, refetch } = useQuery(DEMO_DATA_STATUS_QUERY, { fetchPolicy: 'cache-and-network' });
  const [setHidden, { loading: saving }] = useMutation(SET_DEMO_DATA_HIDDEN_MUTATION);
  const [deactivate] = useMutation(DEACTIVATE_USER_MUTATION);
  const [reactivate] = useMutation(REACTIVATE_USER_MUTATION);
  const [recreate, { loading: recreating }] = useMutation(RECREATE_DEMO_DATA_MUTATION);
  const [deleteAll, { loading: deleting }] = useMutation(DELETE_DEMO_DATA_MUTATION);
  const [dialog, setDialog] = useState(null); // 'hide' | 'show' | 'delete' | 'recreate'

  const st = data?.demoDataStatus;
  const accounts = st?.accounts || [];
  const roles = [...new Map(accounts.map(a => [a.role, a.roleLabel])).entries()];
  const q = search.trim().toLowerCase();
  const shown = accounts.filter(a => (!roleFilter || a.role === roleFilter)
    && (!q || [a.name, a.email, a.roleLabel].some(t => (t || '').toLowerCase().includes(q))));

  const CHANGE_LABELS = { accounts: 'accounts', contracts: 'contracts', offerings: 'offerings', guests: 'guests', wishlists: 'wishlists', bookings: 'bookings' };
  const changes = (from, to) => Object.entries(CHANGE_LABELS)
    .filter(([k]) => to[k] !== from[k]).map(([k, label]) => `${label} ${from[k]} → ${to[k]}`);

  // Run the chosen action on the selection from the dialog
  const runAction = async (scope) => {
    setMessage(null);
    try {
      if (dialog === 'hide' || dialog === 'show') {
        const before = st.active;
        const { data: d } = await setHidden({ variables: { hidden: dialog === 'hide', scope } });
        const s2 = d.setDemoDataHidden;
        const offeringsOnly = !scope.modules.includes('accounts');
        setMessage({ severity: 'success', text: offeringsOnly
          ? (dialog === 'hide' ? `Vendor offerings hidden (${s2.hiddenOfferings} switched off).` : `Vendor offerings shown again (${s2.hiddenOfferings} still hidden).`)
          : dialog === 'hide'
            ? `${before - s2.active} demo account(s) hidden. ${s2.active} of ${s2.total} remain active.`
            : `${s2.active - before} demo account(s) switched back on. ${s2.active} of ${s2.total} active.` });
      } else if (dialog === 'delete') {
        const { data: d } = await deleteAll({ variables: { scope } });
        const { before, after } = d.deleteDemoData;
        const c = changes(before, after);
        setMessage({ severity: 'success', text: `Demo data deleted${c.length ? `: ${c.join(', ')}` : ' (nothing matched)'}. Use "Recreate demo data" to bring it back.` });
      } else if (dialog === 'recreate') {
        const { data: d } = await recreate({ variables: { scope } });
        const { before, after } = d.recreateDemoData;
        const c = changes(before, after);
        setMessage({ severity: 'success', text: `Demo data recreated. ${c.length ? `Restored: ${c.join(', ')}.` : 'Nothing was missing.'} No duplicates were created.` });
      }
      setDialog(null);
      refetch();
    } catch (e) { setMessage({ severity: 'error', text: e.message }); setDialog(null); }
  };
  const toggleAccount = async (a) => {
    setMessage(null);
    try {
      await (a.isActive ? deactivate : reactivate)({ variables: { userId: a.id } });
      refetch();
    } catch (e) { setMessage({ severity: 'error', text: e.message }); }
  };

  return (
    <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <StorageIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>Demo (seed) data</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        The sample accounts that come with the application (brides, grooms, matchmakers, vendors and admins) and all their
        data. Hide them to start clean with only {st?.keptAdmin || 'the main admin'}; nothing is deleted. You can switch
        individual accounts back on below, one by one.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" color="warning" startIcon={<VisibilityOffIcon />} onClick={() => setDialog('hide')}
          disabled={saving || !st || !st.accounts.some(a => !a.kept && a.isActive)}>
          Hide demo data…
        </Button>
        <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setDialog('show')}
          disabled={saving || !st || (!st.accounts.some(a => !a.kept && !a.isActive) && !st.hiddenOfferings)}>
          Show demo data…
        </Button>
        <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={() => setDialog('delete')}
          disabled={deleting || !st || !st.accounts.some(a => !a.kept)}>
          Delete demo data…
        </Button>
        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={() => setDialog('recreate')} disabled={recreating || !st}>
          Recreate demo data…
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Each opens a choice of what to act on (whole accounts, or the data of particular modules such as guests, contracts,
        vendor offerings, bookings, ceremonies, logistics, budget or messages) and for whom (groups or individual accounts).
        Hide switches data off (nothing is deleted); Delete removes it for good; Recreate brings it back without duplicates.
      </Typography>
      {(loading && !data) || recreating || deleting || saving ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {st && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Chip size="small" color={st.hidden ? 'warning' : 'success'} label={st.hidden ? 'Demo data hidden' : 'Demo data shown'} />
            <Typography variant="body2" color="text.secondary">{st.keptAdmin} always stays active.</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {st.active} of {st.total} demo account(s) active. Deactivated accounts cannot sign in, and their vendor
            offerings, profiles and families are not shown anywhere.
          </Typography>
          {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={1.5}>
            <TextField size="small" sx={{ flex: 1 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <TextField select size="small" label="Role" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">All roles</MenuItem>
              {roles.map(([name, label]) => <MenuItem key={name} value={name}>{label}</MenuItem>)}
            </TextField>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                <TableCell>Account</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{a.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.email}</Typography>
                  </TableCell>
                  <TableCell>{a.roleLabel || a.role}</TableCell>
                  <TableCell align="right">
                    {a.kept
                      ? <Chip size="small" color="success" label="Always active" />
                      : <Switch checked={a.isActive} onChange={() => toggleAccount(a)} inputProps={{ 'aria-label': `Active: ${a.email}` }} />}
                  </TableCell>
                </TableRow>
              ))}
              {!shown.length && (
                <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>No accounts match.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}

      {dialog && (
        <DemoDataScopeDialog
          action={dialog}
          status={st}
          busy={saving || deleting || recreating}
          onClose={() => setDialog(null)}
          onConfirm={runAction}
        />
      )}
    </Paper>
  );
}
